import { useEffect, useRef } from "react";
import { useStytchMemberSession } from "@stytch/react/b2b";
import { useNavigate } from "react-router-dom";
import { Spinner } from "@/components/ui/spinner";
import useQueryStringParams from "../../hooks/useQueryStringParams";
import { DEFAULT_LANDING_PATH } from "../../utils/routes";

/**
 * Callback page after organization creation/joining
 * Sets up the session and redirects to dashboard
 */
const AuthCallback: React.FC = (): JSX.Element => {
  const { session } = useStytchMemberSession();
  const navigate = useNavigate();
  const hasSetupSession = useRef(false);
  const { token } = useQueryStringParams(["token"]);

  useEffect(() => {
    const setupSession = () => {
      // Prevent duplicate session setup
      if (hasSetupSession.current) {
        return;
      }

      // If no token provided, redirect to signup
      if (!token) {
        console.error("No session token provided in AuthCallback");
        navigate('/signup', { replace: true });
        return;
      }

      hasSetupSession.current = true;

      try {
        console.log("Setting up session in AuthCallback");

        // Set the session token in cookies where Stytch SDK expects to find it
        // The SDK will automatically pick this up
        const maxAge = 60 * 60 * 24 * 180; // 180 days in seconds

        document.cookie = `stytch_session=${token}; path=/; max-age=${maxAge}; samesite=lax`;
        document.cookie = `stytch_session_jwt=${token}; path=/; max-age=${maxAge}; samesite=lax`;

        // Also store in localStorage as backup
        localStorage.setItem('stytch_session_token', token);

        // Determine redirect destination based on auth mode
        const authMode = localStorage.getItem('authMode');
        const redirectPath = authMode === 'login'
          ? DEFAULT_LANDING_PATH
          : '/configuration/agents';

        // Clean up auth mode flag
        localStorage.removeItem('authMode');

        // Small delay to allow SDK to pick up the cookies, then redirect
        setTimeout(() => {
          // Trigger page reload to let Stytch SDK reinitialize with the new session
          window.location.href = redirectPath;
        }, 500);
      } catch (error) {
        console.error("Session setup failed in AuthCallback:", error);
        navigate('/signup', { replace: true });
      }
    };

    // If already authenticated (session exists), determine redirect based on auth mode
    if (session) {
      const authMode = localStorage.getItem('authMode');
      const redirectPath = authMode === 'login'
        ? DEFAULT_LANDING_PATH
        : '/configuration/agents';

      // Clean up auth mode flag
      localStorage.removeItem('authMode');

      navigate(redirectPath, { replace: true });
      return;
    }

    setupSession();
  }, [token, session, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center py-8">
      <div className="flex flex-col items-center gap-4">
        <Spinner size="xl" />
        <p className="text-lg font-medium">
          Completando autenticación...
        </p>
      </div>
    </div>
  );
};

export default AuthCallback;
